import { supabase } from '@/integrations/supabase/client';

/**
 * Web Push Notifications Support
 * 
 * This module provides web push notification support for the WebApp.
 * For native apps, use the Capacitor PushNotifications plugin instead.
 */

// Public VAPID key (safe to embed in client). Server uses matching private
// key stored as VAPID_PRIVATE_KEY secret in the web-push-send edge function.
const VAPID_PUBLIC_KEY =
  import.meta.env.VITE_VAPID_PUBLIC_KEY ||
  'BP8Jyxk9OTMrVlOABb2hRXnxXvLnbsmHvRbSz9afkY7NJgPl3SOuqlZkAmTu3yaw9FTO4GT-qD1b9gyfx3J2BsY';

let swRegistration: ServiceWorkerRegistration | null = null;
let isSubscribed = false;

/**
 * Check if Web Push is supported
 */
export function isWebPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Check if permission is granted
 */
export function isNotificationPermissionGranted(): boolean {
  if (!('Notification' in window)) return false;
  return Notification.permission === 'granted';
}

/**
 * Request notification permission
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    console.log('[WebPush] Notifications not supported');
    return false;
  }

  const permission = await Notification.requestPermission();
  return permission === 'granted';
}

/**
 * Convert URL base64 to Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPushNotifications(userId: string): Promise<boolean> {
  if (!isWebPushSupported()) {
    console.log('[WebPush] Web push not supported');
    return false;
  }

  if (!VAPID_PUBLIC_KEY) {
    console.error('[WebPush] VAPID public key not configured');
    return false;
  }

  try {
    // Get or create service worker registration
    if (!swRegistration) {
      swRegistration = await navigator.serviceWorker.ready;
    }

    // Check if already subscribed
    const subscription = await swRegistration.pushManager.getSubscription();
    
    if (subscription) {
      console.log('[WebPush] Already subscribed');
      isSubscribed = true;
      // Update subscription on server
      await saveSubscriptionToServer(userId, subscription);
      return true;
    }

    // Subscribe
    const newSubscription = await swRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
    });

    console.log('[WebPush] Subscribed successfully');
    isSubscribed = true;

    // Save to server
    await saveSubscriptionToServer(userId, newSubscription);
    
    return true;
  } catch (error) {
    console.error('[WebPush] Subscription error:', error);
    return false;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPushNotifications(userId: string): Promise<boolean> {
  if (!swRegistration) {
    return false;
  }

  try {
    const subscription = await swRegistration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      console.log('[WebPush] Unsubscribed');
      
      // Remove from server
      await removeSubscriptionFromServer(userId);
    }
    
    isSubscribed = false;
    return true;
  } catch (error) {
    console.error('[WebPush] Unsubscribe error:', error);
    return false;
  }
}

/**
 * Save subscription to Supabase
 */
async function saveSubscriptionToServer(userId: string, subscription: PushSubscription): Promise<void> {
  try {
    const subscriptionJson = subscription.toJSON();
    
    await (supabase.rpc as any)('save_web_push_subscription', {
      p_user_id: userId,
      p_endpoint: subscriptionJson.endpoint,
      p_p256dh: subscriptionJson.keys?.p256dh,
      p_auth: subscriptionJson.keys?.auth,
    });
    
    console.log('[WebPush] Subscription saved to server');
  } catch (error) {
    console.error('[WebPush] Failed to save subscription:', error);
    throw error;
  }
}

/**
 * Remove subscription from Supabase
 */
async function removeSubscriptionFromServer(userId: string): Promise<void> {
  try {
    await (supabase.rpc as any)('remove_web_push_subscription', {
      p_user_id: userId,
    });
    
    console.log('[WebPush] Subscription removed from server');
  } catch (error) {
    console.error('[WebPush] Failed to remove subscription:', error);
  }
}

/**
 * Initialize web push
 */
export async function initializeWebPush(userId: string): Promise<boolean> {
  if (!isWebPushSupported()) {
    return false;
  }

  // Check permission
  if (!isNotificationPermissionGranted()) {
    console.log('[WebPush] Permission not granted');
    return false;
  }

  return subscribeToPushNotifications(userId);
}

/**
 * Show local notification (for testing or foreground notifications)
 */
export function showLocalNotification(title: string, options?: NotificationOptions): void {
  if (!isNotificationPermissionGranted()) {
    return;
  }

  if (swRegistration) {
    swRegistration.showNotification(title, options);
  } else {
    new Notification(title, options);
  }
}

/**
 * Check subscription status
 */
export async function getPushSubscriptionStatus(): Promise<{ supported: boolean; subscribed: boolean; permission: NotificationPermission }> {
  const supported = isWebPushSupported();
  let subscribed = false;
  let permission: NotificationPermission = 'default';

  if (supported) {
    permission = Notification.permission;
    
    if (swRegistration) {
      const subscription = await swRegistration.pushManager.getSubscription();
      subscribed = !!subscription;
    }
  }

  return { supported, subscribed, permission };
}
